import React from 'react';
import { Typography, styled, Button, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toEuroWholeNumber } from '../utils/numberFormat';
import { CashbackAdvantage } from './DetailCalculator';

const FlexRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingBottom: theme.spacing(1),
}));

const Underline = styled('div')({
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
});

const BookNowButton = styled(Button)(({ theme }) => ({
  background: theme.palette.button.cashbackButton.default,
  color: theme.palette.common.white,
  marginTop: theme.spacing(1),
  '&:hover': {
    background: theme.palette.primary.main,
  },
}));

const ShowDetailsButton = styled(Button)(({ theme }) => ({
  color: theme.palette.action.active,
  cursor: 'pointer',
}));

const Spacer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

interface Props {
  cashbackAdvantage: CashbackAdvantage;
  bookNowUrl: string;
  handleCalculateNowButtonClick: () => void;
}

export const AggregatedCommissionBox = ({
  cashbackAdvantage,
  bookNowUrl,
  handleCalculateNowButtonClick,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  return (
    <div>
      <FlexRow>
        <Typography variant="body1" component="div" color="textSecondary">
          <i>
            {t('cashbackCalculator.detailed.aggregatedCommissionBox.refundableAmount')}
            &nbsp;&nbsp;
          </i>
        </Typography>
        <Typography variant="h2" component="div">
          <Underline>{toEuroWholeNumber(cashbackAdvantage.totalPortfolioCommission)}</Underline>
        </Typography>
      </FlexRow>

      <FlexRow>
        <Typography variant="body1" component="div" color="textSecondary">
          <i>
            {t('cashbackCalculator.detailed.aggregatedCommissionBox.totalAgio')}
            &nbsp;&nbsp;
          </i>
        </Typography>
        <Typography variant="h2" component="div">
          <Underline>{toEuroWholeNumber(cashbackAdvantage.totalAgio)}</Underline>
        </Typography>
      </FlexRow>

      <Spacer />

      <FlexRow>
        <BookNowButton variant="contained" size="large" fullWidth={isMobile} href={bookNowUrl}>
          {t('cashbackCalculator.detailed.advantageBox.applyButton')}
        </BookNowButton>
      </FlexRow>

      <FlexRow>
        <ShowDetailsButton
          onClick={() => {
            handleCalculateNowButtonClick();
          }}
        >
          {t('cashbackCalculator.detailed.aggregatedCommissionBox.calculateCashback')}
        </ShowDetailsButton>
      </FlexRow>
    </div>
  );
};
